import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import InventoryIcon from '@mui/icons-material/Inventory';
import Box from '@mui/material/Box';

import { AppProvider, Router } from '@toolpad/core/AppProvider';
import { DashboardLayout } from '@toolpad/core/DashboardLayout';

import { useAuth } from 'contexts/AuthContext';
import { NoticeCountProvider } from 'contexts/NoticeCountContext';

import AppTitle from 'components/layout/AppTitle';
import ToolbarAccount from 'components/layout/ToolbarAccount';
import {
  NoticeToolbarBadge,
  dashboardTheme,
  handleAddAccount,
  handleSwitchAccount,
  signInOut,
  toSession,
} from 'layouts/router/common';
import ScreenGuard from 'routes/ScreenGuard';

/**
 * 資産ドメインのレイアウト。
 * サイドバーナビゲーションは表示しない（Home のダッシュボード + 画面内導線で遷移する）。
 */
const AssessmentLayout: React.FC = () => {
  const { userSession } = useAuth();

  const router = useRouter('/assessment');

  const authentication = React.useMemo(() => signInOut, []);

  const ToolbarAccountSlot = React.useCallback(
    () => (
      <ToolbarAccount
        userSession={userSession}
        onSignOut={signInOut.signOut}
        onSwitchAccount={handleSwitchAccount}
        onAddAccount={handleAddAccount}
      />
    ),
    [userSession],
  );

  const ToolbarActionsSlot = React.useCallback(
    () => (
      <NoticeToolbarBadge
        allowedScreens={userSession.allowedScreens}
        userName={userSession.userName}
      />
    ),
    [userSession.allowedScreens, userSession.userName],
  );

  return (
    <>
      <NoticeCountProvider>
        <AppProvider
          session={toSession(userSession)}
          authentication={authentication}
          navigation={[]}
          router={router}
          theme={dashboardTheme}
        >
          <DashboardLayout
            hideNavigation
            slots={{
              appTitle: AssessmentAppTitle,
              toolbarActions: ToolbarActionsSlot,
              toolbarAccount: ToolbarAccountSlot,
            }}
          >
            <Box
              sx={{
                pt: 1.5,
                pl: 2,
                pr: 2,
                pb: 1.5,
                height: 'calc(100vh - 64px)',
                overflow: 'hidden',
              }}
            >
              <ScreenGuard allowedScreens={userSession.allowedScreens}>
                <Outlet />
              </ScreenGuard>
            </Box>
          </DashboardLayout>
        </AppProvider>
      </NoticeCountProvider>
    </>
  );
};

function useRouter(initialPath: string): Router {
  const navigate = useNavigate();
  const [pathname, setPathname] = React.useState(initialPath);

  const router = React.useMemo(() => {
    return {
      pathname,
      searchParams: new URLSearchParams(),
      navigate: (path: string | URL) => {
        navigate(String(path));
        setPathname(String(path));
      },
    };
  }, [pathname, navigate]);

  return router;
}

function AssessmentAppTitle() {
  return <AppTitle icon={<InventoryIcon />} title="資産管理" color="#283593" />;
}

export default AssessmentLayout;
